import { Unsubscribe } from "firebase/database";
import debounce from "lodash/debounce";
import { ControlRecord } from "../ts/Definitions";

type Listener<T = any> = (arg: T) => void | Promise<void>;

export enum SubscriptionType {
  AllControl = "allControl",
  OwnershipControl = "ownershipControl",
  SessionProgressState = "sessionProgressState",
  UserSettings = "userSettings",
  OwnerSettings = "ownerSettings",
  SystemSettings = "systemSettings",
}

export enum ListenerType {
  ViewChange = "viewChange",
  ControlRecordChanged = "controlRecordChanged",
  Disposed = "disposed",
  Loaded = "loaded",
  AllControlRecords = "allControlRecords",
  // StatefulSettings listeners
  MemberMessages = "memberMessages",
  SentMessages = "sentMessages",
  OwnedItems = "ownedItems",
  OwnerProfiles = "ownerProfiles",
  AuditChanged = "auditChanged",
  RequestAttendance = "requestAttendance",
  Pillories = "pillories",
  ProofUploads = "proofUploads",
  WeeklyTargetProgress = "weeklyTargetProgress",
  TaskLocks = "taskLocks",
  VotingTasks = "votingTasks",
  SystemAuditLog = "systemAuditLog",
  OwnerControlRecord = "ownerControlRecord",
}

/**
 * Manages all subscriptions and listeners for the Settings class
 * Provides centralized subscription lifecycle management
 */
export class SubscriptionManager {
  // Firebase unsubscribe handlers
  private unsubscribers: Map<SubscriptionType, Unsubscribe> = new Map();

  // Listener arrays
  private listeners: Map<ListenerType, Listener[]> = new Map();

  // Debounced listener updaters
  private debouncedUpdateControlListeners: (() => void) & { cancel?: () => void };

  constructor() {
    // Initialize all listener arrays
    Object.values(ListenerType).forEach((type) => {
      this.listeners.set(type, []);
    });

    // Setup debounced control listeners
    this.debouncedUpdateControlListeners = debounce(
      () => this.fireListeners(ListenerType.AllControlRecords),
      1000,
      { leading: false, trailing: true }
    );
  }

  /**
   * Register a Firebase unsubscribe handler
   */
  public registerUnsubscribe(type: SubscriptionType, unsubscribe: Unsubscribe): void {
    // Clean up existing subscription if any
    if (this.unsubscribers.has(type)) {
      this.unsubscribers.get(type)?.();
    }
    this.unsubscribers.set(type, unsubscribe);
  }

  /**
   * Subscribe to a specific event
   */
  public subscribe<T>(type: ListenerType, listener: Listener<T>): void {
    const listenerArray = this.listeners.get(type);
    if (listenerArray && !listenerArray.includes(listener)) {
      listenerArray.push(listener);
    }
  }

  /**
   * Unsubscribe from a specific event
   */
  public unsubscribe<T>(type: ListenerType, listener?: Listener<T>): void {
    const listenerArray = this.listeners.get(type);
    if (!listenerArray) return;

    if (listener) {
      const index = listenerArray.indexOf(listener);
      if (index > -1) {
        listenerArray.splice(index, 1);
      }
    } else {
      // Clear all listeners of this type
      this.listeners.set(type, []);
    }
  }

  /**
   * Fire all listeners of a specific type
   */
  public async fireListeners<T>(type: ListenerType, data?: T): Promise<void> {
    const listenerArray = this.listeners.get(type);
    if (!listenerArray || listenerArray.length === 0) return;

    const promises: Promise<void>[] = [];
    for (const listener of listenerArray) {
      const result = listener(data);
      if (result instanceof Promise) {
        promises.push(result);
      }
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  /**
   * Fire listeners synchronously (for non-async listeners)
   */
  public fireListenersSync<T>(type: ListenerType, data?: T): void {
    const listenerArray = this.listeners.get(type);
    if (!listenerArray || listenerArray.length === 0) return;

    for (const listener of listenerArray) {
      listener(data);
    }
  }

  /**
   * Get count of listeners for a type
   */
  public getListenerCount(type: ListenerType): number {
    return this.listeners.get(type)?.length || 0;
  }

  /**
   * Check if there are any listeners of a type
   */
  public hasListeners(type: ListenerType): boolean {
    return this.getListenerCount(type) > 0;
  }

  /**
   * Fire control record listeners with debouncing
   */
  public fireControlListenersDebounced(allControlRecords: Record<string, ControlRecord>): void {
    this.debouncedUpdateControlListeners?.();
  }

  /**
   * Clear all listeners of all types
   */
  public clearAllListeners(): void {
    this.listeners.forEach((_, type) => {
      this.listeners.set(type, []);
    });
  }

  /**
   * Unsubscribe from a specific subscription type
   */
  public unsubscribeFrom(type: SubscriptionType): void {
    const unsubscribe = this.unsubscribers.get(type);
    if (unsubscribe) {
      unsubscribe();
      this.unsubscribers.delete(type);
    }
  }

  /**
   * Dispose all subscriptions and clear all listeners
   */
  public dispose(): void {
    // Fire disposed listeners first
    this.fireListenersSync(ListenerType.Disposed);

    // Unsubscribe from all Firebase subscriptions
    this.unsubscribers.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.unsubscribers.clear();

    // Cancel debounced functions
    if (this.debouncedUpdateControlListeners?.cancel) {
      this.debouncedUpdateControlListeners.cancel();
    }

    // Clear all listener arrays
    this.clearAllListeners();
  }

  /**
   * Get all listeners of a specific type (for testing/debugging)
   */
  public getListeners<T>(type: ListenerType): Listener<T>[] {
    return [...(this.listeners.get(type) || [])];
  }
}
